import _defineProperty from "@babel/runtime/helpers/esm/defineProperty";
import _classCallCheck from "@babel/runtime/helpers/esm/classCallCheck";
import _createClass from "@babel/runtime/helpers/esm/createClass";
import _inherits from "@babel/runtime/helpers/esm/inherits";
import _createSuper from "@babel/runtime/helpers/esm/createSuper";

var _dec, _dec2, _class, _class2, _temp;

/*
 * The MIT License (MIT)
 *
 * Copyright (c) 2015 - present Instructure, Inc.
 *
 * Permission is hereby granted, free of charge, to any person obtaining a copy
 * of this software and associated documentation files (the "Software"), to deal
 * in the Software without restriction, including without limitation the rights
 * to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
 * copies of the Software, and to permit persons to whom the Software is
 * furnished to do so, subject to the following conditions:
 *
 * The above copyright notice and this permission notice shall be included in all
 * copies or substantial portions of the Software.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
 * IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
 * FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
 * AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
 * LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
 * OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE
 * SOFTWARE.
 */
import React, { Children, Component } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { PositionPropTypes } from '@instructure/ui-position';
import { View } from '@instructure/ui-view';
import { themeable, ThemeablePropTypes } from '@instructure/ui-themeable';
import { safeCloneElement } from '@instructure/ui-react-utils';
import { uid } from '@instructure/uid';
import { testable } from '@instructure/ui-testable';
var styles = {
  componentId: 'cRLeC',
  template: function template(theme) {
    return "\n\n.cRLeC_bGBk{border-radius:".concat(theme.borderRadius || 'inherit', ";box-sizing:border-box;color:").concat(theme.color || 'inherit', ";font-family:").concat(theme.fontFamily || 'inherit', ";font-size:").concat(theme.fontSize || 'inherit', ";font-weight:").concat(theme.fontWeight || 'inherit', ";pointer-events:none;text-align:center;white-space:nowrap}\n\n[dir=ltr] .cRLeC_bGBk,[dir=rtl] .cRLeC_bGBk{text-align:center}\n\n.cRLeC_bGBk:not(.cRLeC_bBTa){position:absolute;z-index:").concat(theme.notificationZIndex || 'inherit', "}\n\n.cRLeC_bGBk:not(.cRLeC_bBTa).cRLeC_dDWY.cRLeC_bXWC{top:calc(-1*").concat(theme.countOffset || 'inherit', ")}\n\n.cRLeC_bGBk:not(.cRLeC_bBTa).cRLeC_dDWY.cRLeC_KksD{top:").concat(theme.notificationOffset || 'inherit', "}\n\n.cRLeC_bGBk:not(.cRLeC_bBTa).cRLeC_bPSM.cRLeC_bXWC{bottom:calc(-1*").concat(theme.countOffset || 'inherit', ")}\n\n.cRLeC_bGBk:not(.cRLeC_bBTa).cRLeC_bPSM.cRLeC_KksD{bottom:").concat(theme.notificationOffset || 'inherit', "}\n\n.cRLeC_bGBk:not(.cRLeC_bBTa).cRLeC_fcMK.cRLeC_bXWC{inset-inline-end:auto;inset-inline-start:calc(-1*").concat(theme.countOffset || 'inherit', ")}\n\n[dir=ltr] .cRLeC_bGBk:not(.cRLeC_bBTa).cRLeC_fcMK.cRLeC_bXWC{left:calc(-1*").concat(theme.countOffset || 'inherit', ");right:auto}\n\n[dir=rtl] .cRLeC_bGBk:not(.cRLeC_bBTa).cRLeC_fcMK.cRLeC_bXWC{left:auto;right:calc(-1*").concat(theme.countOffset || 'inherit', ")}\n\n.cRLeC_bGBk:not(.cRLeC_bBTa).cRLeC_fcMK.cRLeC_KksD{inset-inline-end:auto;inset-inline-start:").concat(theme.notificationOffset || 'inherit', "}\n\n[dir=ltr] .cRLeC_bGBk:not(.cRLeC_bBTa).cRLeC_fcMK.cRLeC_KksD{left:").concat(theme.notificationOffset || 'inherit', ";right:auto}\n\n[dir=rtl] .cRLeC_bGBk:not(.cRLeC_bBTa).cRLeC_fcMK.cRLeC_KksD{left:auto;right:").concat(theme.notificationOffset || 'inherit', "}\n\n.cRLeC_bGBk:not(.cRLeC_bBTa).cRLeC_bXgF.cRLeC_bXWC{inset-inline-end:calc(-1*").concat(theme.countOffset || 'inherit', ");inset-inline-start:auto}\n\n[dir=ltr] .cRLeC_bGBk:not(.cRLeC_bBTa).cRLeC_bXgF.cRLeC_bXWC{left:auto;right:calc(-1*").concat(theme.countOffset || 'inherit', ")}\n\n[dir=rtl] .cRLeC_bGBk:not(.cRLeC_bBTa).cRLeC_bXgF.cRLeC_bXWC{left:calc(-1*").concat(theme.countOffset || 'inherit', ");right:auto}\n\n.cRLeC_bGBk:not(.cRLeC_bBTa).cRLeC_bXgF.cRLeC_KksD{inset-inline-end:").concat(theme.notificationOffset || 'inherit', ";inset-inline-start:auto}\n\n[dir=ltr] .cRLeC_bGBk:not(.cRLeC_bBTa).cRLeC_bXgF.cRLeC_KksD{left:auto;right:").concat(theme.notificationOffset || 'inherit', "}\n\n[dir=rtl] .cRLeC_bGBk:not(.cRLeC_bBTa).cRLeC_bXgF.cRLeC_KksD{left:").concat(theme.notificationOffset || 'inherit', ";right:auto}\n\n.cRLeC_bGBk:not(.cRLeC_bBTa).cRLeC_eCdq.cRLeC_bXgF.cRLeC_bXWC,.cRLeC_bGBk:not(.cRLeC_bBTa).cRLeC_eCdq.cRLeC_fcMK.cRLeC_bXWC{top:calc(50% - ").concat(theme.size || 'inherit', "/2)}\n\n.cRLeC_bGBk:not(.cRLeC_bBTa).cRLeC_eCdq.cRLeC_bXgF.cRLeC_KksD,.cRLeC_bGBk:not(.cRLeC_bBTa).cRLeC_eCdq.cRLeC_fcMK.cRLeC_KksD{top:calc(50% - ").concat(theme.sizeNotification || 'inherit', "/2)}\n\n.cRLeC_bGBk:not(.cRLeC_bBTa).cRLeC_eCdq.cRLeC_fcMK.cRLeC_bXWC{inset-inline-end:calc(100% - ").concat(theme.countOffset || 'inherit', ");inset-inline-start:auto}\n\n[dir=ltr] .cRLeC_bGBk:not(.cRLeC_bBTa).cRLeC_eCdq.cRLeC_fcMK.cRLeC_bXWC{left:auto;right:calc(100% - ").concat(theme.countOffset || 'inherit', ")}\n\n[dir=rtl] .cRLeC_bGBk:not(.cRLeC_bBTa).cRLeC_eCdq.cRLeC_fcMK.cRLeC_bXWC{left:calc(100% - ").concat(theme.countOffset || 'inherit', ");right:auto}\n\n.cRLeC_bGBk:not(.cRLeC_bBTa).cRLeC_eCdq.cRLeC_fcMK.cRLeC_KksD{inset-inline-end:auto;inset-inline-start:calc(-1*").concat(theme.sizeNotification || 'inherit', "/2)}\n\n[dir=ltr] .cRLeC_bGBk:not(.cRLeC_bBTa).cRLeC_eCdq.cRLeC_fcMK.cRLeC_KksD{left:calc(-1*").concat(theme.sizeNotification || 'inherit', "/2);right:auto}\n\n[dir=rtl] .cRLeC_bGBk:not(.cRLeC_bBTa).cRLeC_eCdq.cRLeC_fcMK.cRLeC_KksD{left:auto;right:calc(-1*").concat(theme.sizeNotification || 'inherit', "/2)}\n\n.cRLeC_bGBk:not(.cRLeC_bBTa).cRLeC_eCdq.cRLeC_bXgF.cRLeC_bXWC{inset-inline-end:auto;inset-inline-start:calc(100% - ").concat(theme.countOffset || 'inherit', ")}\n\n[dir=ltr] .cRLeC_bGBk:not(.cRLeC_bBTa).cRLeC_eCdq.cRLeC_bXgF.cRLeC_bXWC{left:calc(100% - ").concat(theme.countOffset || 'inherit', ");right:auto}\n\n[dir=rtl] .cRLeC_bGBk:not(.cRLeC_bBTa).cRLeC_eCdq.cRLeC_bXgF.cRLeC_bXWC{left:auto;right:calc(100% - ").concat(theme.countOffset || 'inherit', ")}\n\n.cRLeC_bGBk:not(.cRLeC_bBTa).cRLeC_eCdq.cRLeC_bXgF.cRLeC_KksD{inset-inline-end:calc(-1*").concat(theme.sizeNotification || 'inherit', "/2);inset-inline-start:auto}\n\n[dir=ltr] .cRLeC_bGBk:not(.cRLeC_bBTa).cRLeC_eCdq.cRLeC_bXgF.cRLeC_KksD{left:auto;right:calc(-1*").concat(theme.sizeNotification || 'inherit', "/2)}\n\n[dir=rtl] .cRLeC_bGBk:not(.cRLeC_bBTa).cRLeC_eCdq.cRLeC_bXgF.cRLeC_KksD{left:calc(-1*").concat(theme.sizeNotification || 'inherit', "/2);right:auto}\n\n.cRLeC_bGBk.cRLeC_bXWC{-webkit-padding-end:").concat(theme.padding || 'inherit', ";-webkit-padding-start:").concat(theme.padding || 'inherit', ";line-height:").concat(theme.size || 'inherit', ";min-width:").concat(theme.size || 'inherit', ";padding-inline-end:").concat(theme.padding || 'inherit', ";padding-inline-start:").concat(theme.padding || 'inherit', "}\n\n[dir=ltr] .cRLeC_bGBk.cRLeC_bXWC{padding-left:").concat(theme.padding || 'inherit', ";padding-right:").concat(theme.padding || 'inherit', "}\n\n[dir=rtl] .cRLeC_bGBk.cRLeC_bXWC{padding-left:").concat(theme.padding || 'inherit', ";padding-right:").concat(theme.padding || 'inherit', "}\n\n.cRLeC_bGBk.cRLeC_KksD{height:").concat(theme.sizeNotification || 'inherit', ";width:").concat(theme.sizeNotification || 'inherit', "}\n\n.cRLeC_zGXc{background-color:").concat(theme.colorDanger || 'inherit', "}\n\n.cRLeC_zGXc.cRLeC_fdSp:before{border-color:").concat(theme.colorDanger || 'inherit', "}\n\n.cRLeC_cOXX{background-color:").concat(theme.colorSuccess || 'inherit', "}\n\n.cRLeC_cOXX.cRLeC_fdSp:before{border-color:").concat(theme.colorSuccess || 'inherit', "}\n\n.cRLeC_bXiG{background-color:").concat(theme.colorPrimary || 'inherit', "}\n\n.cRLeC_bXiG.cRLeC_fdSp:before{border-color:").concat(theme.colorPrimary || 'inherit', "}\n\n@keyframes cRLeC_fdSp{to{opacity:0.9;transform:scale(1)}}\n\n.cRLeC_fdSp{position:relative}\n\n.cRLeC_fdSp:before{animation-direction:alternate;animation-duration:1s;animation-iteration-count:4;animation-name:cRLeC_fdSp;border:").concat(theme.pulseBorderThickness || 'inherit', " solid;border-radius:").concat(theme.borderRadius || 'inherit', ";box-sizing:border-box;content:\"\";height:calc(100% + 0.5rem);inset-inline-end:auto;inset-inline-start:-0.25rem;opacity:0;position:absolute;top:-0.25rem;transform:scale(0.75);width:calc(100% + 0.5rem)}\n\n[dir=ltr] .cRLeC_fdSp:before{left:-0.25rem;right:auto}\n\n[dir=rtl] .cRLeC_fdSp:before{left:auto;right:-0.25rem}\n\n.cRLeC_gasz{box-sizing:border-box;position:relative}\n\n.cRLeC_gasz svg{display:block}");
  },
  'root': 'cRLeC_bGBk',
  'standalone': 'cRLeC_bBTa',
  'positioned--top': 'cRLeC_dDWY',
  'count': 'cRLeC_bXWC',
  'notification': 'cRLeC_KksD',
  'positioned--bottom': 'cRLeC_bPSM',
  'positioned--start': 'cRLeC_fcMK',
  'positioned--end': 'cRLeC_bXgF',
  'positioned--center': 'cRLeC_eCdq',
  'danger': 'cRLeC_zGXc',
  'pulse': 'cRLeC_fdSp',
  'success': 'cRLeC_cOXX',
  'primary': 'cRLeC_bXiG',
  'wrapper': 'cRLeC_gasz'
};
import theme from "./theme.js";
/**
---
category: components
---
**/

var Badge = (_dec = testable(), _dec2 = themeable(theme, styles), _dec(_class = _dec2(_class = (_temp = _class2 = /*#__PURE__*/function (_Component) {
  _inherits(Badge, _Component);

  var _super = _createSuper(Badge);

  function Badge(props) {
    var _this;

    _classCallCheck(this, Badge);

    _this = _super.call(this, props);
    _this._defaultId = uid('Badge');
    return _this;
  }

  _createClass(Badge, [{
    key: "countOverflow",
    value: function countOverflow() {
      var _this$props = this.props,
          count = _this$props.count,
          countUntil = _this$props.countUntil;

      if (countUntil > 1 && count >= countUntil) {
        return true;
      } else {
        return false;
      }
    }
  }, {
    key: "renderOutput",
    value: function renderOutput() {
      var _this$props2 = this.props,
          count = _this$props2.count,
          countUntil = _this$props2.countUntil,
          formatOverflowText = _this$props2.formatOverflowText,
          formatOutput = _this$props2.formatOutput,
          type = _this$props2.type; // If the badge count is >= than the countUntil limit, format the badge text
      // via the formatOverflowText function prop

      var formattedCount = type === 'count' && this.countOverflow() ? formatOverflowText(count, countUntil) : count;

      if (typeof formatOutput === 'function') {
        return formatOutput(formattedCount);
      } else {
        return type === 'count' ? formattedCount : null;
      }
    }
  }, {
    key: "renderBadge",
    value: function renderBadge() {
      var _classnames;

      var _this$props3 = this.props,
          count = _this$props3.count,
          margin = _this$props3.margin,
          pulse = _this$props3.pulse,
          placement = _this$props3.placement,
          standalone = _this$props3.standalone,
          type = _this$props3.type,
          variant = _this$props3.variant;
      return /*#__PURE__*/React.createElement(View, {
        margin: standalone ? margin : 'none',
        className: classnames((_classnames = {}, _defineProperty(_classnames, styles.root, true), _defineProperty(_classnames, styles[type], type), _defineProperty(_classnames, styles[variant], variant), _defineProperty(_classnames, styles['positioned--top'], placement.indexOf('top') > -1), _defineProperty(_classnames, styles['positioned--bottom'], placement.indexOf('bottom') > -1), _defineProperty(_classnames, styles['positioned--start'], placement.indexOf('start') > -1), _defineProperty(_classnames, styles['positioned--end'], placement.indexOf('end') > -1), _defineProperty(_classnames, styles['positioned--center'], placement.indexOf('center') > -1), _defineProperty(_classnames, styles.standalone, standalone), _defineProperty(_classnames, styles.pulse, pulse), _classnames)),
        title: type === 'count' && this.countOverflow() ? count : null,
        id: !standalone ? this._defaultId : null,
        display: standalone ? 'inline-block' : 'block'
      }, this.renderOutput());
    }
  }, {
    key: "renderChildren",
    value: function renderChildren() {
      var _this2 = this;

      return Children.map(this.props.children, function (child) {
        return safeCloneElement(child, {
          'aria-describedby': _this2._defaultId
        });
      });
    }
  }, {
    key: "render",
    value: function render() {
      var _this$props4 = this.props,
          margin = _this$props4.margin,
          elementRef = _this$props4.elementRef,
          standalone = _this$props4.standalone,
          as = _this$props4.as;

      if (standalone) {
        return this.renderBadge();
      } else {
        return /*#__PURE__*/React.createElement(View, {
          as: as,
          margin: margin,
          elementRef: elementRef,
          className: styles.wrapper,
          display: "inline-block"
        }, this.renderChildren(), this.renderBadge());
      }
    }
  }]);

  Badge.displayName = "Badge";
  return Badge;
}(Component), _class2.propTypes = {
  count: PropTypes.number,

  /**
   * The number at which the count gets truncated by
   * formatOverflowText. For example, a countUntil of 100
   * would stop the count at 99.
   */
  countUntil: PropTypes.number,
  children: PropTypes.element,

  /**
   * Render Badge as a counter (`count`) or as a smaller dot (`notification`) with
   * no count number displayed.
   */
  type: PropTypes.oneOf(['count', 'notification']),

  /**
   * Render Badge as an inline html element that is not positioned relative
   * to a child.
   */
  standalone: PropTypes.bool,

  /**
   * Make the Badge slowly pulse twice to get the user's attention.
   */
  pulse: PropTypes.bool,
  variant: PropTypes.oneOf(['primary', 'success', 'danger']),

  /**
   * Supported values are `top start`, `top end`, `end center`, `bottom end`,
   * `bottom start`, and `start center`
   */
  placement: PositionPropTypes.placement,

  /**
   * Valid values are `0`, `none`, `auto`, `xxx-small`, `xx-small`, `x-small`,
   * `small`, `medium`, `large`, `x-large`, `xx-large`. Apply these values via
   * familiar CSS-like shorthand. For example: `margin="small auto large"`.
   */
  margin: ThemeablePropTypes.spacing,

  /**
   * provides a reference to the underlying html root element
   */
  elementRef: PropTypes.func,
  formatOverflowText: PropTypes.func,
  formatOutput: PropTypes.func,
  as: PropTypes.elementType // eslint-disable-line react/require-default-props

}, _class2.defaultProps = {
  count: void 0,
  children: null,
  countUntil: void 0,
  margin: void 0,
  formatOutput: void 0,
  standalone: false,
  type: 'count',
  variant: 'primary',
  pulse: false,
  placement: 'top end',
  elementRef: function elementRef(el) {},
  formatOverflowText: function formatOverflowText(count, countUntil) {
    return "".concat(countUntil - 1, " +");
  }
}, _temp)) || _class) || _class);
export default Badge;
export { Badge };